import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';

import { ButtonModule, DIALOG_DATA, DialogRef, IconModule } from '@hglobal/fin-ui';
import { TranslateModule } from '@ngx-translate/core';

import { TransactionsComponent, WalletCardComponent } from '@kr/components';
import { ExchangeDialog } from '@kr/components/exchange/exchange.dialog';
import { environment } from '@kr/environment';
import { WalletDetailsDialogData, WalletDetailsTab } from '@kr/models';
import { CurrencyBalanceFormatPipe } from '@kr/pipes';
import { ClipboardService } from '@kr/services';

import { WALLET_DETAILS_API, WalletDetailsApiService, WalletDetailsMockApiService } from './api';
import { MassPayOutDialog } from '../mass-pay-out/mass-pay-out.dialog';
import { TransferDialog } from '../transfer/transfer.dialog';
import { DeleteWalletFailedComponent } from './components/failed';
import { DeleteWalletSuccessComponent } from './components/success';
import { WalletEditDialog } from './components/wallet-edit/wallet-edit.dialog';
import { WalletUsersComponent } from './components/wallet-users/wallet-users.component';
import { WalletDetailsStore } from './wallet-details.store';

@Component({
  selector: 'kr-wallet-details',
  templateUrl: './wallet-details.component.html',
  styleUrls: ['./wallet-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    IconModule,
    ButtonModule,
    WalletCardComponent,
    TransactionsComponent,
    WalletUsersComponent,
    CurrencyBalanceFormatPipe,
    DeleteWalletFailedComponent,
    DeleteWalletSuccessComponent,
  ],
  providers: [
    TransferDialog,
    MassPayOutDialog,
    WalletEditDialog,
    ExchangeDialog,
    WalletDetailsStore,
    {
      provide: WALLET_DETAILS_API,
      useClass: environment.useMocks ? WalletDetailsMockApiService : WalletDetailsApiService,
    },
  ],
})
export class WalletDetailsComponent implements OnInit {
  readonly #dialogRef = inject(DialogRef);
  readonly #transferDialog = inject(TransferDialog);
  readonly #massPayOutDialog = inject(MassPayOutDialog);
  readonly #exchangeDialog = inject(ExchangeDialog);
  readonly #walletDetailsStore = inject(WalletDetailsStore);
  readonly data = inject<WalletDetailsDialogData>(DIALOG_DATA);
  clipboard = inject(ClipboardService);

  isDeleteWalletSuccess = this.#walletDetailsStore.isDeleteWalletSuccess;
  deleteWalletError = this.#walletDetailsStore.deleteWalletError;

  wallets = this.data.wallets;
  allWallets = this.data.allWallets;
  tab = this.data.tab || 'wallet';
  providerId = this.data.providerId;
  initialWalletName = this.data.wallet.name;
  walletEdited = this.#walletDetailsStore.walletEdited;
  wallet = this.#walletDetailsStore.wallet;

  ngOnInit(): void {
    this.#walletDetailsStore.getWalletDetails(this.data.wallet);
  }

  setTab(tab: WalletDetailsTab): void {
    this.tab = tab;
  }

  openTransferDialog(): void {
    this.#transferDialog.open(this.wallet(), this.wallets);
  }

  openMassPayOutDialog(): void {
    this.#massPayOutDialog.open(this.wallet());
  }

  openWalletEditDialog(): void {
    this.#walletDetailsStore.openWalletEditDialog();
  }

  openExchangeDialog(): void {
    this.#exchangeDialog.open(this.wallet(), this.allWallets);
  }

  close(): void {
    this.#dialogRef.close(this.walletEdited());
  }
}
